<template>
  <v-btn
    class="mbtn font-weight-regular"
    prepend-icon="ct:arrow-left"
    @click="stepBack"
  >
    {{ t("buyout.appraisal.go_back") }}
  </v-btn>
  <div v-if="authStore.isPartner && !authStore.isPartnerBrandStore">
    <v-alert
      class="vAlert--errorAlt mt-16"
    >
      <template #prepend>
        <v-icon icon="ct:circle-exclamation" />
      </template>
      <div>
        <span>
          {{ t("static.bonuses.customer_data_available_only_for_brand_stores") }}
        </span>
      </div>
    </v-alert>
  </div>
  <template v-else>
    <div class="app__title app__title--h3">
      {{ t('buyout.appraisal.personal_info_for_contract') }}
    </div>
    <div class="buyout__summary">
      <div class="wrapper">
        <v-form
          ref="form"
          v-model="validPersonalInfoForm"
          class="app__form"
        >
          <div class="formItem formItem--1x12 formItem--header">
            <div class="app__title app__title--h4">
              {{ authStore.isPartner ? t('buyout.appraisal.contact_data_customer') : t('buyout.appraisal.contact_data') }}
            </div>
            <v-dialog
              v-model="loginDialog"
              content-class="popup"
            >
              <template #activator="{ props }">
                <v-btn
                  v-if="!authStore.isLoggedIn"
                  class="mbtn mbtn--trans"
                  size="large"
                  v-bind="props"
                >
                  {{ t('buyout.common.log_in') }}
                </v-btn>
              </template>
              <auth-login-form>
                <template #step1_close>
                  <div
                    class="popup__close cursor-pointer"
                    @click="loginDialog = false"
                  >
                    <v-icon icon="ct:cross" size="x-small" />
                  </div>
                </template>
                <template #step2_close>
                  <div
                    class="popup__close cursor-pointer"
                    @click="loginDialog = false"
                  >
                    <v-icon icon="ct:cross" size="x-small" />
                  </div>
                </template>
                <template #otp="{phone, email, token}">
                  <auth-otp-field
                    :phone="phone"
                    :email="email"
                    :token="token"
                    :is-from-buyout="true"
                    @close-dialog="loginDialog = false; refreshDataAfterLogIn()"
                  />
                </template>
              </auth-login-form>
            </v-dialog>
          </div>
          <div class="formItem formItem--1x6">
            <v-text-field
              v-model="buyoutPersonalData.email"
              :label="t('form.common.email')"
              required
              :error-messages="get(buyoutPersonalDataErrors, 'email')"
              :rules="[rules.required, rules.isEmail]"
              type="email"
              name="customer_data_email"
              @input="buyoutStore.resetPersonalDataErrors('email')"
            />
          </div>
          <div class="formItem formItem--1x6">
            <phone-field
              :key="phoneFieldKey"
              v-model="buyoutPersonalData.phone_number!.suffix"
              name="customer_data_phone_number"
              placeholder="123 456 789"
              :label="t('form.common.phone_number')"
              :error-messages="get(buyoutPersonalDataErrors, 'phone_number')"
              :invalid-message="t('form.validation.phone_number')"
              :rules="[rules.required, rules.isPhoneNumber]"
              :default-country="selectedCountry.iso2"
              @input="buyoutStore.resetPersonalDataErrors('phone_number')"
              @country-changed="handleCountryChange"
            />
          </div>
          <div class="formItem formItem--1x12 formItem--noLabel">
            <div class="app__title app__title--h4">
              {{ t('buyout.appraisal.personal_data') }}
            </div>
          </div>
          <div class="formItem formItem--1x6">
            <v-text-field
              v-model="buyoutPersonalData.first_name"
              :label="t('form.common.name')"
              required
              :error-messages="get(buyoutPersonalDataErrors, 'first_name')"
              :placeholder="t('form.placeholders.name_ph')"
              :rules="[rules.required]"
              name="customer_data_name"
              @input="buyoutStore.resetPersonalDataErrors('first_name')"
            />
          </div>
          <div class="formItem formItem--1x6">
            <v-text-field
              v-model="buyoutPersonalData.last_name"
              :label="t('form.common.last_name')"
              required
              :error-messages="get(buyoutPersonalDataErrors, 'last_name')"
              :placeholder="t('form.placeholders.surname_ph')"
              :rules="[rules.required]"
              name="customer_data_last_name"
              @input="buyoutStore.resetPersonalDataErrors('last_name')"
            />
          </div>
          <div class="formItem formItem--1x12 formItem--noLabel formItem--variableWidth">
            <div class="vRadioGroup vRadioGroup--toggle">
              <v-radio-group
                v-model="buyoutPersonalData.gender"
                :inline="true"
                :rules="[rules.required]"
                :error-messages="get(buyoutPersonalDataErrors, 'gender')"
                @input="buyoutStore.resetPersonalDataErrors('gender')"
              >
                <div class="app__label color--grayTone3">
                  {{ t('form.common.gender') }}
                </div>
                <v-radio :label="t('form.profile.personal_info.man')" value="m" />
                <v-radio :label="t('form.profile.personal_info.woman')" value="f" />
              </v-radio-group>
            </div>
            <div class="formItem">
              <v-text-field
                v-model="buyoutPersonalData.personal_number"
                :label="t('form.common.rc_birth_date')"
                required
                :error-messages="get(buyoutPersonalDataErrors, 'personal_number')"
                :rules="[rules.required]"
                name="customer_data_personal_number"
                @input="buyoutStore.resetPersonalDataErrors('personal_number')"
              />
            </div>
          </div>
          <div class="formItem formItem--1x6">
            <v-autocomplete
              v-model="buyoutPersonalData.nationality"
              :items="countryChoices"
              :loading="countriesLoading"
              :label="t('form.common.nationality')"
              :disabled="countriesLoading"
              item-title="short_name"
              item-value="id"
              required
              :error-messages="get(buyoutPersonalDataErrors, 'nationality')"
              :rules="[rules.required]"
              name="customer_data_nationality"
              @update:model-value="buyoutStore.resetPersonalDataErrors('personal_number');"
              @change="buyoutStore.resetPersonalDataErrors('nationality');"
            />
          </div>
          <div class="formItem formItem--1x6">
            <v-text-field
              v-model="buyoutPersonalData.place_of_birth"
              :label="t('form.common.place_of_birth')"
              required
              :error-messages="get(buyoutPersonalDataErrors, 'place_of_birth')"
              :rules="[rules.required]"
              name="customer_data_place_of_birth"
              @input="buyoutStore.resetPersonalDataErrors('place_of_birth')"
            />
          </div>
          <div class="formItem formItem--1x12 formItem--noLabel">
            <div class="app__title app__title--h4">
              {{ t('buyout.appraisal.op_or_passport') }}
            </div>
          </div>
          <div class="formItem formItem--1x4">
            <v-text-field
              v-model="buyoutPersonalData.id_number"
              :label="t('form.common.identity_card_number')"
              required
              :error-messages="get(buyoutPersonalDataErrors, 'id_number')"
              :rules="[rules.required]"
              name="customer_data_id_number"
              @input="buyoutStore.resetPersonalDataErrors('id_number')"
            />
          </div>
          <div class="formItem formItem--1x4">
            <v-text-field
              v-model="buyoutPersonalData.id_issued_by"
              :placeholder="t('form.placeholders.identity_released_by_ph')"
              :label="t('form.common.identity_released_by')"
              required
              :error-messages="get(buyoutPersonalDataErrors, 'id_issued_by')"
              :rules="[rules.required]"
              name="customer_data_id_issued_by"
              @input="buyoutStore.resetPersonalDataErrors('id_issued_by')"
            />
          </div>
          <div class="formItem formItem--1x4">
            <v-text-field
              v-model="buyoutPersonalData.id_valid_to"
              :placeholder="t('form.placeholders.identity_valid_to_ph')"
              type="date"
              :label="t('form.common.id_valid_to')"
              required
              :error-messages="get(buyoutPersonalDataErrors, 'id_valid_to')"
              :rules="[rules.required]"
              :min="minDate"
              :class="buyoutPersonalData.id_valid_to ? `` : `color--grayTone4`"
              name="customer_data_id_valid_to"
              @change="buyoutStore.resetPersonalDataErrors('id_valid_to')"
            />
          </div>
          <div class="formItem formItem--1x12 formItem--noLabel">
            <div class="app__title app__title--h4">
              {{ t('buyout.appraisal.permanent_address') }}
            </div>
          </div>
          <!-- @vue-ignore -->
          <common-address-form v-model="buyoutPersonalData" :error-messages="buyoutPersonalDataErrors" required @error-reset="errorReset" />
          <dialog-address-validation
            v-if="addressValidationError !== null"
            :validation-result="addressValidationError"
            :original-address="buyoutPersonalData"
            persistent
            @cancel="addressValidationError = null"
            @continue="setAddressAndTryToContinue(null)"
            @continue-with-address="setAddressAndTryToContinue"
          />
        </v-form>
        <v-btn
          class="mbtn mbtn--green mbtn--multiline"
          size="x-large"
          :loading="userDataLoading || countryLoading || moveToDeliveryLoading"
          @click="moveToDelivery"
        >
          {{ t('buyout.appraisal.continue_to_delivery') }}
        </v-btn>
      </div>
      <buyout-device-summary-tile :is-new-device-process="isNewDeviceProcess" />
    </div>
  </template>
</template>

<script setup lang="ts">
import { get, isEmpty } from "lodash-es";
import { storeToRefs } from "pinia";
import { VForm } from "vuetify/components";
import type { AddressValidationResult } from "cashtec-common/src/components/dialog/AddressValidation.vue";

const api = useApi();
const buyoutStore = useBuyoutStore();
const authStore = useAuthStore();
const { t, locale } = useI18n();
const { notify } = useNotification();
const commonStore = useCommonStore();
const { selectedCountry, phoneNumberPrefix } = storeToRefs(commonStore);

const {
  personalDataStep,
  buyoutPersonalData,
  buyoutPersonalDataErrors,
  mainBuyoutStep,
  userBuyoutCode,
  accountAlreadyExists,
  buyoutLoadedFromUrl
} = storeToRefs(buyoutStore);

const countryStore = useCountryStore();

const {
  countryChoices, countriesLoading
} = storeToRefs(countryStore)

const { rules } = useFormValidators();

const form = ref<VForm | null>(null);
const validPersonalInfoForm = ref <boolean>(false);
const countryLoading = ref<boolean>(false);
const loginDialog = ref<boolean>(false);
const userDataLoading = ref<boolean>(false);
const moveToDeliveryLoading = ref<boolean>(false);
const emailLowercase = computed<string>(() => buyoutPersonalData.value.email.toLowerCase());
const addressValidationError = ref<AddressValidationResult|null>(null);
const wasAddressConfirmed = ref(false);
const phoneFieldKey = ref<number>(0);

const componentProps = withDefaults(defineProps<{
  isNewDeviceProcess?: boolean
}>(), {
  isNewDeviceProcess: false
});

useMoveToTopOfPage();

loadUserData();

function loadUserData () {
  userDataLoading.value = true;
  if (authStore.isLoggedIn && !(authStore.isPartnerBrandStore) && !buyoutLoadedFromUrl.value) {
    getUserAdditionalData();
    getPersonalData();
  }
  userDataLoading.value = false;
}

interface ResponseData {
  registration_number: string;
  already_exists: boolean | string[];
}

async function createPersonalData () {
  const validForm = await form.value?.validate();
  if (!validForm?.valid) {
    await useGoTo(".v-input.v-input--error", { offset: -150 });
    moveToDeliveryLoading.value = false;
    return;
  }
  buyoutPersonalData!.value!.language = locale.value;

  const customerInfo = {
    ...buyoutPersonalData!.value,
    phone_number: `${buyoutPersonalData.value.phone_number.prefix}${buyoutPersonalData.value.phone_number.suffix}`,
    email: emailLowercase.value,
    was_address_confirmed: wasAddressConfirmed.value
  };

  const body: {
  customer_info: any;
  registration_number: string;
  customer_id?: number | null;
  } = {
    customer_info: customerInfo,
    registration_number: userBuyoutCode!.value || "",
    customer_id: buyoutStore.getBuyoutCustomerId()
  };

  const { data, error } = await api.call<Record<string, string[]> | ResponseData>(api.commonUrls.buyout.CREATE_PERSONAL_DATA(), {
    method: "PUT",
    body: body
  });
  if (data.value) {
    if (data.value.already_exists === true) {
      accountAlreadyExists.value = true;
    }
    personalDataStep.value += 1;
  }
  if (error.value) {
    if (error.value?.data) {
      buyoutPersonalDataErrors.value = error.value?.data.customer_info;
      if (Object.prototype.hasOwnProperty.call(error.value?.data, "x_address")) {
        addressValidationError.value = error.value!.data.x_address;
      }
      moveToDeliveryLoading.value = false;
      await useGoTo(".v-input.v-input--error", { offset: -100 });
    } else {
      notify({
        title: t("form.common.error"),
        text: t("general.error_sending_request"),
        type: "error"
      });
    }
  }
}

async function setAddressAndTryToContinue (newAddress: {street_and_number:string, postal_code:string, city:string}|null) {
  if (newAddress !== null) {
    buyoutPersonalData!.value.street_and_number = newAddress.street_and_number;
    buyoutPersonalData!.value.postal_code = newAddress.postal_code;
    buyoutPersonalData!.value.city = newAddress.city;
  } else {
    // set the address to be absolutely correct only when the user explicitly confirms it,
    // otherwise validate it once more to be sure
    wasAddressConfirmed.value = true;
  }

  if (!isEmpty(buyoutPersonalDataErrors.value)) {
    addressValidationError.value = null;
    await useGoTo(".v-input.v-input--error", { offset: -100 });
  } else {
    await moveToDelivery();
  }
}

async function initializeChat () {
  const validForm = await form.value?.validate();
  if (!validForm?.valid) {
    return;
  }
  if (userBuyoutCode!.value) {
    const { error } = await api.call<Record<string, string[]>>(api.commonUrls.chat.INITIALIZE_CHAT_ROOM(userBuyoutCode!.value), {
      method: "POST"
    });
    if (error.value) {
      buyoutPersonalDataErrors.value = error.value?.data.customer_info;
    }
  }
}
async function moveToDelivery () {
  addressValidationError.value = null;
  moveToDeliveryLoading.value = true;
  await createPersonalData();
  await initializeChat();
  moveToDeliveryLoading.value = false;
}

function stepBack () {
  if(componentProps.isNewDeviceProcess){
    mainBuyoutStep.value -= 1;
  } else {
    mainBuyoutStep.value -= buyoutStore.showBonuses ? 1 : 2;
  }
}

async function getPersonalData () {
  if (!buyoutStore.personalDataLoaded) {
    const { data, error } = await api.call<GetPersonalDataInfo>(api.commonUrls.user.USER_DATA());
    if (error.value) {
      notify({
        title: t("form.common.error"),
        text: error.value!.data?.detail ?? t("general.error_sending_request"),
        type: "error"
      });
    }
    if (data.value) {
      buyoutPersonalData.value = { ...buyoutPersonalData.value };
      buyoutPersonalData.value.first_name = data.value.first_name;
      buyoutPersonalData.value.last_name = data.value.last_name;
      buyoutPersonalData.value.email = data.value.email;
      phoneNumberPrefix.value = data.value.phone_prefix;
      buyoutPersonalData.value.phone_number.prefix = data.value.phone_prefix;
      buyoutPersonalData.value.phone_number.suffix = data.value.phone_suffix;
      buyoutStore.personalDataLoaded = true;
      phoneFieldKey.value += 1; // hack to force rerender of the phoneField
    }
  }
}

async function getUserAdditionalData () {
  if (!buyoutStore.additionalUserDataLoaded) {
    const { data, error } = await api.call<PersonalDataInfo>(api.commonUrls.user.USER_ADDITIONAL_DATA());
    if (error.value) {
      notify({
        title: t("form.common.error"),
        text: error.value!.data?.detail ?? t("general.error_sending_request"),
        type: "error"
      });
    }
    if (data.value) {
      buyoutPersonalData.value = { ...buyoutPersonalData.value, ...data.value };
      buyoutStore.additionalUserDataLoaded = true;
    }
  }
}

function refreshDataAfterLogIn () {
  // TO DO - get them with one call and without pickup_address
  getPersonalData();
  getUserAdditionalData();
  notify({
    title: t("form.login.login_successfully"),
    type: "success"
  });
}

for (const field of ["street_and_number", "postal_code", "city", "country"]) {
  watch(() => get(buyoutPersonalData, field), () => buyoutStore.resetPersonalDataErrors(field));
}

const minDate = computed(() => {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  return new Date(date).toISOString().slice(0, 10);
});

function handleCountryChange (selectedCountry: Country) {
  phoneNumberPrefix.value = `+${selectedCountry.dialCode}`;
  buyoutPersonalData.value.phone_number!.prefix = `+${selectedCountry.dialCode}`;
}

function errorReset(fieldName: string) {
  buyoutStore.resetPersonalDataErrors(fieldName);
}

</script>
